import * as React from 'react'
import {
  CheckCircle2, CircleDashed, Clock, HelpCircle, Lock,
  ShieldAlert, Trash2, TriangleAlert,
} from 'lucide-react'
import type { LifecycleStatus, MatchStatus } from '@/lib/types'
import { useRuleFields } from '@/lib/queries'
import { cn, formatDateTime, formatUTC, relativeTime } from '@/lib/utils'
import { Badge } from './badge'
import { Tooltip } from './tooltip'

/**
 * Lifecycle status presentation (Appendix B).
 *
 * §5.8 forbids colour as the sole carrier of meaning, so every badge renders an
 * icon *and* a text label; the colour only reinforces them.
 */
const STATUS_META: Record<
  LifecycleStatus,
  { label: string; icon: React.ComponentType<{ className?: string }>; className: string; hint: string }
> = {
  registered: {
    label: '已注册',
    icon: Lock,
    className: 'bg-emerald-500/12 text-emerald-700 dark:text-emerald-400',
    hint: '存在有效注册的可信证据',
  },
  expired: {
    label: '已过期',
    icon: Clock,
    className: 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
    hint: '外部证据显示已过期，但仍可能续费或恢复；不等于可注册',
  },
  redemptionPeriod: {
    label: '赎回期',
    icon: TriangleAlert,
    className: 'bg-orange-500/15 text-orange-700 dark:text-orange-400',
    hint: '注册局赎回期或等价状态',
  },
  pendingDelete: {
    label: '删除等待期',
    icon: Trash2,
    className: 'bg-red-500/12 text-red-700 dark:text-red-400',
    hint: '删除等待期；释放窗口为估算，不构成承诺',
  },
  available: {
    label: '可注册',
    icon: CheckCircle2,
    className: 'bg-teal-500/15 text-teal-700 dark:text-teal-400',
    hint: '来自 Registrar/权威渠道的近期肯定结果；仍以注册商页面为准',
  },
  reserved: {
    label: '保留/禁止',
    icon: ShieldAlert,
    className: 'bg-slate-500/15 text-slate-700 dark:text-slate-300',
    hint: '保留、禁止或特殊政策状态',
  },
  unknown: {
    label: '未知',
    icon: HelpCircle,
    className: 'bg-muted text-muted-foreground',
    hint: '本次无法可靠判断；不代表可注册，也不代表已注册',
  },
}

export function StatusBadge({
  status,
  source,
  confirmedAt,
  confidence,
  className,
}: {
  status: LifecycleStatus
  source?: string
  confirmedAt?: string | null
  confidence?: number
  className?: string
}) {
  const meta = STATUS_META[status] ?? STATUS_META.unknown
  const Icon = meta.icon

  // §7.1: an availability claim must always travel with its source and the
  // time it was confirmed. The tooltip is where that lives in a dense table.
  const tooltip = (
    <div className="space-y-1">
      <p className="font-medium">{meta.label}</p>
      <p>{meta.hint}</p>
      {source && <p>来源：{sourceLabel(source)}</p>}
      <p>确认时间：{confirmedAt ? formatUTC(confirmedAt) : '尚未确认'}</p>
      {confidence != null && <p>置信度：{confidence}/100</p>}
    </div>
  )

  return (
    <Tooltip content={tooltip}>
      <span
        className={cn(
          'inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium',
          meta.className,
          className,
        )}
      >
        <Icon className="h-3 w-3" aria-hidden />
        {meta.label}
      </span>
    </Tooltip>
  )
}

export function sourceLabel(source: string): string {
  switch (source) {
    case 'rdap': return 'RDAP（注册局目录服务）'
    case 'registrar': return 'Registrar Availability API'
    case 'zone': return 'Zone File 观察'
    case 'dns': return 'DNS 观察'
    case 'whois': return 'WHOIS（回退来源）'
    case 'user_query': return '用户查询'
    case 'generator': return '规则生成'
    case 'admin': return '管理员录入'
    case 'system': return '平台内部'
    case '': return '尚未确认'
    default: return source
  }
}

const MATCH_META: Record<MatchStatus, { label: string; variant: 'success' | 'warning' | 'muted' | 'danger'; hint: string }> = {
  matched: {
    label: '已确认命中',
    variant: 'success',
    hint: '完整规则树已确认命中',
  },
  pending_verification: {
    label: '待确认',
    variant: 'warning',
    // §6.2.1: the whole point of surfacing this state is to stop a user
    // reading an unconfirmed candidate as "available".
    hint: '本地条件已通过，外部状态尚未确认；请勿据此认为可注册',
  },
  no_longer_matched: {
    label: '确认后不符合',
    variant: 'muted',
    hint: '外部状态确认后不再满足规则',
  },
  stale: {
    label: '确认超时',
    variant: 'muted',
    hint: '多次确认仍无法判定，已停止消耗外部预算',
  },
}

export function MatchStatusBadge({ status }: { status: MatchStatus }) {
  const meta = MATCH_META[status] ?? MATCH_META.stale
  return (
    <Tooltip content={meta.hint}>
      <Badge variant={meta.variant}>{meta.label}</Badge>
    </Tooltip>
  )
}

/**
 * Scheduling tiers (§7.7), mirroring monitor.DefaultTierPolicies.
 *
 * The intervals are numbers rather than prose because two places need them: the
 * badge that explains the schedule, and the freshness column that judges
 * whether a domain is behind it. Those two used to be written separately — the
 * badge said L0 is checked every 7–30 days while the column beside it flagged
 * anything past 24 hours — so the table said the schedule was working and the
 * row next to it said the same domain was overdue.
 */
export const TIER_POLICY = [
  { label: 'L0 冷', minHours: 7 * 24, maxHours: 30 * 24, note: '未关注 / 稳定 / 低价值' },
  { label: 'L1 常规', minHours: 24, maxHours: 72, note: '规则命中或普通监控' },
  { label: 'L2 热', minHours: 1, maxHours: 6, note: '过期 / 赎回期 / 高价值' },
  { label: 'L3 关键', minHours: 6, maxHours: 12, note: '删除等待期，释放窗口另行加密' },
]

/** Human interval, e.g. "24–72 小时" or "7–30 天". */
function tierInterval(policy: typeof TIER_POLICY[number]): string {
  if (policy.minHours >= 24) return `${policy.minHours / 24}–${policy.maxHours / 24} 天`
  return `${policy.minHours}–${policy.maxHours} 小时`
}

/** How stale a confirmation may get on this tier before it is worth flagging. */
export function staleAfterHoursForTier(tier?: number): number {
  return (TIER_POLICY[tier ?? 1] ?? TIER_POLICY[1]).maxHours
}

/** Scheduling tier badge (§7.7). */
export function TierBadge({ tier }: { tier: number }) {
  const policy = TIER_POLICY[tier]
  const meta = policy
    ? { label: policy.label, hint: `${policy.note}：${tierInterval(policy)}` }
    : { label: `L${tier}`, hint: '' }

  return (
    <Tooltip content={meta.hint}>
      <Badge variant={tier >= 2 ? 'warning' : 'muted'}>{meta.label}</Badge>
    </Tooltip>
  )
}

/**
 * Freshness indicator for the "最后确认" column. §5.4 asks for stale values to
 * be highlighted rather than shown as if they were current.
 */
export function FreshnessCell({
  lastResolvedAt,
  tier,
  nextCheckAt,
  watched,
  staleAfterHours,
  timezone,
}: {
  lastResolvedAt?: string | null
  /** Scheduling tier; sets the threshold when staleAfterHours is not given. */
  tier?: number
  /** Shown in the tooltip so the column answers "when will it be checked". */
  nextCheckAt?: string | null
  /** Watched domains run on their own fixed interval, not on a tier. */
  watched?: boolean
  staleAfterHours?: number
  timezone?: string
}) {
  if (!lastResolvedAt) {
    return (
      <Tooltip content="该域名尚未经过外部确认">
        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
          <CircleDashed className="h-3 w-3" aria-hidden /> 未确认
        </span>
      </Tooltip>
    )
  }

  // A watched domain is checked on a fixed short interval that the tier system
  // does not govern, so judging it by a tier ceiling would never fire and would
  // mean nothing if it did.
  const threshold = staleAfterHours ?? (watched ? 1 : staleAfterHoursForTier(tier))
  const ageHours = (Date.now() - new Date(lastResolvedAt).getTime()) / 3_600_000
  const stale = ageHours > threshold
  const thresholdLabel = threshold >= 48 ? `${Math.round(threshold / 24)} 天` : `${threshold} 小时`

  return (
    <Tooltip
      content={
        <div className="space-y-0.5">
          <p>本地时间：{formatDateTime(lastResolvedAt, timezone)}</p>
          <p>UTC：{formatUTC(lastResolvedAt)}</p>
          {nextCheckAt && <p>计划下次检查：{formatDateTime(nextCheckAt, timezone)}</p>}
          {stale ? (
            <p className="text-amber-300">已超过该层级上限 {thresholdLabel} 未确认</p>
          ) : (
            <p className="text-muted-foreground">在该层级的计划范围内（上限 {thresholdLabel}）</p>
          )}
        </div>
      }
    >
      <span
        className={cn(
          'inline-flex items-center gap-1 text-xs',
          stale ? 'font-medium text-amber-700 dark:text-amber-400' : 'text-muted-foreground',
        )}
      >
        {stale && <TriangleAlert className="h-3 w-3" aria-hidden />}
        {relativeTime(lastResolvedAt)}
      </span>
    </Tooltip>
  )
}

/**
 * Feature tag list with a bounded number of visible chips.
 *
 * Labels come from the engine's own field catalogue rather than a table kept
 * here, so the domain library and the rule builder can never disagree about
 * what a tag means. `alphanumeric` is the case that made this matter: it means
 * "LDH-clean", not "has both letters and digits", and a local table had it
 * labelled in a way that read as the latter.
 */
export function FeatureTags({ tags, max = 3 }: { tags?: string[]; max?: number }) {
  const labels = useFeatureLabels()
  if (!tags?.length) return <span className="text-xs text-muted-foreground">—</span>

  const visible = withoutImplied(tags)
  const shown = visible.slice(0, max)
  const rest = visible.slice(max)
  const describe = (t: string) => labels.get(t)?.label ?? t

  return (
    <span className="flex flex-wrap gap-1">
      {shown.map((t) => (
        <Tooltip key={t} content={labels.get(t)?.hint}>
          <Badge variant="secondary" className="font-normal">{describe(t)}</Badge>
        </Tooltip>
      ))}
      {rest.length > 0 && (
        <Tooltip content={rest.map(describe).join('、')}>
          <Badge variant="outline" className="font-normal">+{rest.length}</Badge>
        </Tooltip>
      )}
    </span>
  )
}

/**
 * Tags implied by another tag already present. Showing both is noise in a
 * dense table: "纯数字" already tells you the label is ASCII and hyphen-free,
 * and "存在三重复" already implies "存在双重复".
 *
 * Suppression is display-only — the underlying feature bits are unchanged, so
 * filtering and rule matching still see every tag.
 */
const IMPLIED_BY: Record<string, string[]> = {
  alphanumeric: ['pure_digits', 'pure_letters'],
  repeat_2: ['repeat_3', 'all_same'],
  repeat_3: ['all_same'],
  palindrome: ['all_same'],
}

const HIDDEN_FIXED_PATTERN_TAGS = new Set(['ABAB', 'ABCABC', 'AABB', 'ABBA'])

function withoutImplied(tags: string[]): string[] {
  const visible = tags.filter((tag) => !HIDDEN_FIXED_PATTERN_TAGS.has(tag))
  const present = new Set(visible)
  return visible.filter((t) => !(IMPLIED_BY[t] ?? []).some((implier) => present.has(implier)))
}

/** Builds tag -> {label, hint} from the cached rule-field catalogue. */
export function useFeatureLabels(): Map<string, { label: string; hint?: string }> {
  const { data } = useRuleFields()
  return React.useMemo(() => {
    const map = new Map<string, { label: string; hint?: string }>()
    const spec = data?.fields.find((f) => f.name === 'feature')
    for (const o of spec?.enum ?? []) map.set(o.value, { label: o.label, hint: o.hint })
    return map
  }, [data])
}


/** GFW risk chip. §18.7 forbids conflating this with registration status. */
export function GfwBadge({ status }: { status: string }) {
  if (!status || status === 'unknown') return null
  const meta: Record<string, { label: string; variant: 'success' | 'warning' | 'danger' }> = {
    clear: { label: 'GFW 正常', variant: 'success' },
    suspected: { label: 'GFW 疑似', variant: 'warning' },
    polluted: { label: 'GFW 污染', variant: 'danger' },
  }
  const m = meta[status]
  if (!m) return null
  return (
    <Tooltip content="GFW 风险信号独立于注册状态，不构成法律意义上的判定，可申请复测。">
      <Badge variant={m.variant}>{m.label}</Badge>
    </Tooltip>
  )
}
